import React, { useState } from 'react';
import { Link } from '@ipc/ipc-core';
import { PropTypes } from 'prop-types';
import { Button } from '@ipc/ipc-components';

import RecordsListTable from '../../components/custom/RecordsListTable';
import { Table, Download } from '../../elements';
import SecureComponent from '../../components/custom/SecureComponent';
import AlaTableView from '../../components/custom/AlaTableView';
import {
  ROLE_ADMIN, ROLE_CARRIER, ROLE_OPERATOR, hasAccess,
} from '../../utils/access';
import { TopButtonsWrapper, AppContentInner, AppContentTitle } from '../../styles';

import { usePresettlements } from './hooks';
import {
  TABLE_COLUMNS, TABLE_CARRIER_COLUMN, TABLE_OPERATOR_COLUMN, DOWNLOAD_COLUMNS, DOWNLOAD_CARRIER_COLUMN, DOWNLOAD_OPERATOR_COLUMN, FILE_NAME, TITLE,
  TABLE_IMPC_COLUMN,
} from './constants';
import { RecordsListTableWrapper } from './styles';
import ImpcFilterModal from './components/ImpcFilterModal';
import { customPrimaryButton } from '../../styles/themes';
import UpdateApprovalStatusModal from '../../components/custom/UpdateApprovalStatusModal';

export default function PreSettlementList({ history, location }) {
  const tableColumns = [...TABLE_COLUMNS];
  const downloadColumns = [...DOWNLOAD_COLUMNS];
  const [data, requestId, updateRequestId, loading, deleteItem, updateStatusRecords, userPartyCode, updateData, originalTableData] = usePresettlements(location);
  const [selected, updateSelected] = useState([]);
  const [statusApprovalModal, updateStatusApprovalModal] = useState({});
  const [recRerender, updateRecRerender] = useState(false);
  const [impcFilterModal, updateImpcFilterModal] = useState({});
  const [copyData, setCopyData] = useState([]);
  const [isImpcFiltered, setImpcFiltered] = useState(false);

  const canDeleteItemSettlement = item => item.requestType === 'IMMEDIATE' && item.creatorCode === userPartyCode;

  if (hasAccess([ROLE_ADMIN, ROLE_OPERATOR])) {
    tableColumns.splice(2, 0, TABLE_CARRIER_COLUMN);
    downloadColumns.splice(2, 0, DOWNLOAD_CARRIER_COLUMN);
  }

  if (hasAccess([ROLE_ADMIN, ROLE_CARRIER])) {
    tableColumns.splice(2, 0, TABLE_OPERATOR_COLUMN);
    downloadColumns.splice(2, 0, DOWNLOAD_OPERATOR_COLUMN);
  }

  if (hasAccess([ROLE_ADMIN, ROLE_OPERATOR, ROLE_CARRIER])) {
    tableColumns.splice(3, 0, TABLE_IMPC_COLUMN);
  }

  const itemsStatusUpdate = (items, status) => () => {
    updateStatusApprovalModal({
      open: true,
      text: status,
      items,
    });
  };

  const dismissUpdateApprovalStatusHandler = () => {
    updateStatusApprovalModal({ open: false });
  };

  const confirmUpdateStatusApprovalModal = () => () => {
    const { items, text: status } = statusApprovalModal;
    updateStatusRecords(items, status);
    updateStatusApprovalModal({ open: false });
  };

  const removeDuplicates = arr => Array.from(new Set(arr.map(a => a.value)))
    .map(value => arr.find(a => a.value === value));
  const filterByIMPC = () => {
    const options = removeDuplicates([...new Set(originalTableData.map(item => item.impc).flat(Infinity))]);
    updateImpcFilterModal({ open: true, options });
  };

  const confirmImpcFilterModalHandler = (selectedImpc) => {
    const copy = originalTableData;
    setCopyData(copy);
    const filteredData = selectedImpc.length === 0 ? []
      : copy.filter(tableDate => tableDate.impc.some(impc => selectedImpc.indexOf(impc) > -1));
    updateData(filteredData);
    setImpcFiltered(true);
    updateImpcFilterModal({ open: false });
  };

  const dismissImpcFilterModalHandler = () => {
    updateImpcFilterModal({});
  };

  const clearFilterHandler = () => {
    updateData(isImpcFiltered ? copyData : data);
    setImpcFiltered(false);
    updateImpcFilterModal({ open: false });
  };

  const viewRecords = id => () => {
    updateRecRerender(!recRerender);
    updateRequestId(id);
    history.replace(`${location.pathname}?requestId=${id}`, location.state);

    setTimeout(() => {
      const contentElement = document.getElementsByClassName('appContent');

      if (contentElement && contentElement[0]) {
        contentElement[0].scrollTo(0, contentElement[0].scrollHeight);
      }
    }, 300);
  };
  const onRowClickHandler = (state, { original: { id } }) => {
    viewRecords(id)();
  };

  const isScheduledRequestOfStatus = (item, status) => item.requestType && item.status
    && item.requestType.indexOf('SCHEDULED') !== -1 && item.status.indexOf(status) === -1;

  const isSelectedScheduledType = () => !selected.length || data
    .filter(dataItem => selected.includes(dataItem.id))
    .some(item => item.requestType.indexOf('SCHEDULED') === -1);

  const actions = item => (
    <>
      <div onClick={viewRecords(item.id)} data-testid="view-records">View Records</div>
      {isScheduledRequestOfStatus(item, 'Approved')
      && <div onClick={itemsStatusUpdate([item.id], 'APPROVED')} data-testid="approve-action">Approve</div>
      }
      {isScheduledRequestOfStatus(item, 'Disapproved')
      && <div onClick={itemsStatusUpdate([item.id], 'DISAPPROVED')} data-testid="disapprove-action">Disapprove</div>
      }
      <Link to={`/pre-settlement/${item.id}/copy`}> Copy</Link>
      <SecureComponent roles={[ROLE_ADMIN]} hasPermission={canDeleteItemSettlement(item)}>
        <div onClick={deleteItem(item.id, data)}>Delete</div>
      </SecureComponent>
    </>
  );

  const trPropsHandler = (state, rowInfo) => {
    if (rowInfo && rowInfo.row && rowInfo.original && +rowInfo.original.id === +requestId) {
      return {
        className: 'HighlightedRow',
      };
    }
    return {};
  };

  return (
    <AppContentInner>
      <AppContentTitle>View settlement Requests</AppContentTitle>

      <UpdateApprovalStatusModal
        modal={statusApprovalModal}
        confirmHandler={confirmUpdateStatusApprovalModal}
        dismissHandler={dismissUpdateApprovalStatusHandler}
      />

      <ImpcFilterModal
        modal={impcFilterModal}
        confirmHandler={confirmImpcFilterModalHandler}
        dismissHandler={dismissImpcFilterModalHandler}
        clearHandler={clearFilterHandler}
        isFiltered={isImpcFiltered}
      />

      <TopButtonsWrapper>
        <Download columns={downloadColumns} fileName={FILE_NAME} title={TITLE} items={data} selected={selected} />
        <Button onClick={itemsStatusUpdate(selected, 'DISAPPROVED')} disabled={isSelectedScheduledType()} data-testid="selected-disapprove">Disapprove</Button>
        <Button onClick={itemsStatusUpdate(selected, 'APPROVED')} disabled={isSelectedScheduledType()} data-testid="selected-approve">Approve</Button>
        <Button onClick={() => filterByIMPC()} theme={isImpcFiltered && customPrimaryButton}>Filter on Origin IMPC(s)</Button>
        <AlaTableView />
      </TopButtonsWrapper>
      <Table
        className="SettlementList"
        columns={tableColumns}
        data={data}
        loading={loading}
        actions={actions}
        defaultPageSize={10}
        onSelect={updateSelected}
        onRowClick={onRowClickHandler}
        getTrPropsHandler={trPropsHandler}
      />

      {requestId && (
        <RecordsListTableWrapper>
          <h2 className="AppContentTitle">Settlement Records List</h2>
          <RecordsListTable requestId={requestId} rerender={recRerender} />
        </RecordsListTableWrapper>
      )}
    </AppContentInner>
  );
}

PreSettlementList.propTypes = {
  location: PropTypes.shape(),
  history: PropTypes.shape(),
};
